import { useCallback, useEffect, useRef, useState } from "react";
import { setTabIndexes } from "./setTabIndexes";

/*
 * This hook is used to expand and collapse a section of text
 * when a button is clicked. It also sets the tabindex of links in the
 * text to -1 when collapsed and 0 when expanded.
 */
const useExpand = (
  onExpand?: (expandedHeight: number | undefined) => void,
  onClose?: (minimizedHeight: number | undefined) => void
) => {
  const [expandedHeight, setExpandedHeight] = useState<undefined | number>(
    undefined
  );
  const [minimizedHeight, setMinimizedHeight] = useState<undefined | number>(
    undefined
  );
  const [isExpanded, setIsExpanded] = useState(false);
  const expandNodeRef = useRef<HTMLDivElement | null>(null);

  const calculateHeights = useCallback(() => {
    const node = expandNodeRef.current;
    if (!node) return;

    const scrollHeight = node.scrollHeight || 0;
    const offsetHeight = node.offsetHeight || 0;

    setExpandedHeight(scrollHeight);
    setMinimizedHeight(offsetHeight);
  }, []);

  const textRefFunction = useCallback((node: HTMLDivElement | null) => {
    if (!node || expandNodeRef.current) return;
    expandNodeRef.current = node;
    setTabIndexes(node, "-1");

    calculateHeights();
  }, [calculateHeights]);

  useEffect(() => {
    const node = expandNodeRef.current;
    if (!node) return;

    const resizeObserver = new ResizeObserver(() => {
      calculateHeights();
    });

    resizeObserver.observe(node);

    return () => resizeObserver.disconnect();
  }, [calculateHeights]);

  useEffect(() => {
    const handleResize = () => {
      calculateHeights();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [calculateHeights]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      calculateHeights();
    }, 0);
    return () => clearTimeout(timeout);
  }, [calculateHeights]);

  useEffect(() => {
    const node = expandNodeRef.current;
    if (!node) return;

    if (isExpanded) {
      setTabIndexes(node, "0");
      onExpand?.(expandedHeight);
    } else {
      setTabIndexes(node, "-1");
      onClose?.(minimizedHeight);
    }
  }, [isExpanded, expandedHeight, minimizedHeight, onExpand, onClose]);

  return {
    minimizedHeight,
    expandedHeight,
    textRefFunction,
    expandNodeRef,
    isExpanded,
    setIsExpanded,
  };
};

export default useExpand;
